import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import SearchIcon from '@mui/icons-material/Search'
import ExpandIcon from '@mui/icons-material/UnfoldMoreOutlined'
import RecolherIcon from '@mui/icons-material/UnfoldLessOutlined'
import DownloadIcon from '@mui/icons-material/FileDownloadOutlined'
import services from '../../services'
import { useNotification } from '../../hooks/useNotification.js'
import Janela from '../componentes/Janela'
import FiltroAmbiente from './FiltroAmbiente'
import SolicitacaoDetalhe from './SolicitacaoDetalhe'
import { exportarSolicitacoes } from './exportar'
import { useBuscaNasConversas } from './useBuscaNasConversas'
import {
  ambientesDosPedidos, atrasada, COLUNAS, dataDoPrazo, filtrar, motivoValido, movimento, PRIORIDADES, porColuna, Resumo, rotuloPrioridade, rotuloStatus, rotuloTipo, TIPOS
} from './comum'
import '../gestao/gestao.scss'
import './solicitacoes.scss'

/** Concluídas e canceladas aparecem só as dos últimos 30 dias; as abertas, todas. */
const DIAS_DAS_ENCERRADAS = 30

const inicial = (nome: string | null) => (nome || '?').trim().charAt(0).toUpperCase()

/**
 * Gestão › Fila de atendimento: o kanban de todas as solicitações. Arrastar o cartão muda o status (cancelar pede o motivo);
 * encerrada só volta pelo Reabrir, no detalhe. Quem não pode arrastar (celular) usa o "Mover para" do próprio cartão.
 */
export default function Kanban() {
  const { notify } = useNotification()
  const [params, setParams] = useSearchParams()
  const [lista, setLista] = useState<Resumo[] | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  const [busca, setBusca] = useState('')
  const [tipo, setTipo] = useState('')
  const [prioridade, setPrioridade] = useState('')
  const [responsavel, setResponsavel] = useState('')
  const [ambienteId, setAmbienteId] = useState('')
  const [soAtrasadas, setSoAtrasadas] = useState(false)
  const [soBloqueadas, setSoBloqueadas] = useState(false)

  const [recolhidas, setRecolhidas] = useState<Record<string, boolean>>({ cancelado: true })
  const [arrastando, setArrastando] = useState<number | null>(null)
  const [sobre, setSobre] = useState<string | null>(null)

  const [cancelando, setCancelando] = useState<Resumo | null>(null)
  const [motivo, setMotivo] = useState('')
  const [salvando, setSalvando] = useState(false)

  const aberta = Number(params.get('abrir')) || null

  const carregar = useCallback(async () => {
    const r = await services.api.solicitacoesListar(true, DIAS_DAS_ENCERRADAS)
    if (r.fail) { setErro(r.error || 'Não foi possível carregar a fila.'); return }
    setErro(null)
    setLista(r.data)
  }, [])

  useEffect(() => { carregar() }, [carregar])

  const nasConversas = useBuscaNasConversas(busca, true)
  const filtrados = useMemo(
    () => filtrar(lista || [], { busca, tipo, status: '', prioridade, soAtrasadas, responsavel, ambienteId, soBloqueadas, nasConversas }),
    [lista, busca, tipo, prioridade, soAtrasadas, responsavel, ambienteId, soBloqueadas, nasConversas]
  )
  const colunas = useMemo(() => porColuna(filtrados), [filtrados])

  // Os ambientes que aparecem nos pedidos da tela, para o filtro.
  const ambientes = useMemo(() => ambientesDosPedidos(lista || []), [lista])

  const responsaveis = useMemo(() => {
    const mapa = new Map<number, string>()
    ;(lista || []).forEach(s => { if (s.responsavelId != null) mapa.set(s.responsavelId, s.responsavelNome || `Pessoa #${s.responsavelId}`) })
    return Array.from(mapa.entries()).sort((a, b) => a[1].localeCompare(b[1], 'pt-BR'))
  }, [lista])

  /** Muda o status. Mostra o cartão na coluna nova na hora e, se o servidor recusar, volta e explica. */
  const aplicarStatus = async (card: Resumo, para: string, motivoDoCancelamento?: string): Promise<boolean> => {
    const antes = lista
    setLista(l => (l || []).map(s => (s.id === card.id ? { ...s, status: para } : s)))
    const r = await services.api.solicitacaoMudarStatus(card.id, para, motivoDoCancelamento)
    if (r.fail) {
      setLista(antes)
      notify(r.error || 'Não foi possível mudar o status.', { variant: 'error' })
      return false
    }
    setLista(l => (l || []).map(s => (s.id === card.id ? { ...s, status: r.data.status, atualizadoEm: r.data.atualizadoEm, bloqueado: r.data.bloqueado, bloqueioMotivo: r.data.bloqueioMotivo } : s)))
    return true
  }

  const mover = (card: Resumo, para: string) => {
    const m = movimento(card.status, para)
    if (m === 'igual') return
    if (m === 'encerrada') { notify('Solicitação encerrada: abra o detalhe e use Reabrir.', { variant: 'info' }); return }
    if (m === 'cancelar') { setMotivo(''); setCancelando(card); return }
    aplicarStatus(card, para)
  }

  const confirmarCancelamento = async () => {
    if (!cancelando) return
    setSalvando(true)
    const ok = await aplicarStatus(cancelando, 'cancelado', motivo.trim())
    setSalvando(false)
    if (ok) setCancelando(null)
  }

  const soltar = (e: React.DragEvent, coluna: string) => {
    e.preventDefault()
    setSobre(null)
    const id = Number(e.dataTransfer.getData('text/plain')) || arrastando
    setArrastando(null)
    const card = (lista || []).find(s => s.id === id)
    if (card) mover(card, coluna)
  }

  const abrir = (id: number) => setParams({ abrir: String(id) })
  const fechar = () => { setParams({}); carregar() }

  return (
    <div className="ng">
      <div className="ng-cab">
        <h1>Fila de atendimento</h1>
        <div className="ng-acoes">
          <button type="button" className="ng-btn" disabled={filtrados.length === 0} onClick={() => exportarSolicitacoes(filtrados)} title="Baixa os cartões que estão na tela como planilha, que abre no Excel"><DownloadIcon fontSize="small" /> Exportar Excel</button>
        </div>
      </div>
      <p className="ng-explic">
        Todas as solicitações por etapa. Arraste o cartão para mudar o status. Concluídas e canceladas aparecem só as dos últimos {DIAS_DAS_ENCERRADAS} dias.
      </p>

      <div className="ng-filtros">
        <label className="ng-busca"><SearchIcon fontSize="small" />
          <input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar por número, título, pessoa ou texto da conversa" aria-label="Buscar solicitação" />
        </label>
        <select className="ng-select" value={tipo} onChange={e => setTipo(e.target.value)} aria-label="Tipo">
          <option value="">Todos os tipos</option>{TIPOS.map(t => <option key={t.id} value={t.id}>{t.rotulo}</option>)}
        </select>
        <select className="ng-select" value={responsavel} onChange={e => setResponsavel(e.target.value)} aria-label="Responsável">
          <option value="">Todos os responsáveis</option>
          <option value="nenhum">Sem responsável</option>
          {responsaveis.map(([id, nome]) => <option key={id} value={id}>{nome}</option>)}
        </select>
        <FiltroAmbiente opcoes={ambientes} valor={ambienteId} aoMudar={setAmbienteId} />
        <select className="ng-select" value={prioridade} onChange={e => setPrioridade(e.target.value)} aria-label="Prioridade">
          <option value="">Todas as prioridades</option>{PRIORIDADES.map(p => <option key={p.id} value={p.id}>{p.rotulo}</option>)}
        </select>
        <label className="sol-check"><input type="checkbox" checked={soAtrasadas} onChange={e => setSoAtrasadas(e.target.checked)} /> Só atrasadas</label>
        <label className="sol-check"><input type="checkbox" checked={soBloqueadas} onChange={e => setSoBloqueadas(e.target.checked)} /> Só bloqueadas</label>
      </div>

      {erro && <div className="sol-aviso" role="alert">{erro}</div>}
      {!lista && !erro && <p className="ng-explic">Carregando…</p>}

      {lista && (
        <div className="kb-quadro">
          {COLUNAS.map(coluna => {
            const cartoes = colunas[coluna]
            const fechada = !!recolhidas[coluna]
            return (
              <section key={coluna} className={`kb-coluna${fechada ? ' recolhida' : ''}${sobre === coluna ? ' sobre' : ''}`} aria-label={rotuloStatus(coluna)}
                       onDragOver={e => { e.preventDefault(); if (sobre !== coluna) setSobre(coluna) }}
                       onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setSobre(null) }}
                       onDrop={e => soltar(e, coluna)}>
                <header className="kb-cab">
                  <b>{rotuloStatus(coluna)}</b>
                  <span className="kb-n" aria-label={`${cartoes.length} solicitações`}>{cartoes.length}</span>
                  <button type="button" className="kb-ico" aria-expanded={!fechada} aria-label={`${fechada ? 'Abrir' : 'Recolher'} a coluna ${rotuloStatus(coluna)}`}
                          onClick={() => setRecolhidas({ ...recolhidas, [coluna]: !fechada })}>{fechada ? <ExpandIcon fontSize="inherit" /> : <RecolherIcon fontSize="inherit" />}</button>
                </header>
                {!fechada && (
                  <div className="kb-cartoes">
                    {cartoes.length === 0 && <p className="kb-vazio">Nada aqui.</p>}
                    {cartoes.map(s => (
                      <article key={s.id} className={`kb-card${s.bloqueado ? ' bloqueado' : ''}${atrasada(s) ? ' atrasado' : ''}${arrastando === s.id ? ' arrastando' : ''}`}
                               draggable onDragStart={e => { e.dataTransfer.setData('text/plain', String(s.id)); e.dataTransfer.effectAllowed = 'move'; setArrastando(s.id) }}
                               onDragEnd={() => { setArrastando(null); setSobre(null) }}>
                        <button type="button" className="kb-abrir" onClick={() => abrir(s.id)} aria-label={`Abrir #${s.id} ${s.titulo}`}>
                          <b>#{s.id} · {s.titulo}</b>
                          {s.ambienteNome && <small className="kb-amb">{s.ambienteNome}</small>}
                          <small>{rotuloTipo(s.tipo)} · {s.solicitanteNome || 'alguém'}</small>
                        </button>
                        <div className="kb-selos">
                          {(s.prioridade === 'alta' || s.prioridade === 'urgente') && <span className={`ng-pill ${s.prioridade === 'urgente' ? 'inativo' : 'alerta'}`}>{rotuloPrioridade(s.prioridade)}</span>}
                          {s.esforco && <span className="ng-pill" title="Esforço">{s.esforco}</span>}
                          {s.prazo && <span className={`ng-pill${atrasada(s) ? ' inativo' : ''}`}>{atrasada(s) ? 'Atrasada · ' : ''}{dataDoPrazo(s.prazo)}</span>}
                          {s.bloqueado && <span className="ng-pill alerta" title={s.bloqueioMotivo || undefined}>Bloqueada</span>}
                          <span className={`kb-avatar${s.responsavelNome ? '' : ' vazio'}`} title={s.responsavelNome || 'Sem responsável'}>{s.responsavelNome ? inicial(s.responsavelNome) : '–'}</span>
                        </div>
                        {!['concluido', 'cancelado'].includes(s.status) && (
                          <select className="kb-mover" value={s.status} onChange={e => mover(s, e.target.value)} aria-label={`Mover #${s.id} para`}>
                            {COLUNAS.map(c => <option key={c} value={c}>{rotuloStatus(c)}</option>)}
                          </select>
                        )}
                      </article>
                    ))}
                  </div>
                )}
              </section>
            )
          })}
        </div>
      )}

      <Janela aberto={!!cancelando} titulo="Cancelar solicitação" sub={cancelando ? `#${cancelando.id} · ${cancelando.titulo}` : undefined} botao="Cancelar solicitação"
              perigo desabilitado={!motivoValido(motivo)} salvando={salvando} aoConfirmar={confirmarCancelamento} aoFechar={() => setCancelando(null)}>
        <label className="ng-campo"><span>Motivo do cancelamento (quem pediu vai ver)</span>
          <textarea rows={3} value={motivo} maxLength={1000} onChange={e => setMotivo(e.target.value)} aria-label="Motivo do cancelamento" />
        </label>
      </Janela>

      <SolicitacaoDetalhe id={aberta} aoFechar={fechar} />
    </div>
  )
}
